/* eslint-disable import/exports-last */
/* eslint-disable import/prefer-default-export */
/* eslint-disable consistent-default-export-name/default-export-match-filename */
import calculateColumnWidths from '../calculateColumnWidths'
import {
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useColumns, useExpandable, useIsPivot, usePivotEntities, useTableId } from './selectors'
import { useIsTouchDevice, useResizeObserver } from '../../../../hooks'
import type { ColumnWidth } from '../calculateColumnWidths'

const ResizeContext = createContext<{
  columnWidths: Array<ColumnWidth>
  resizing: boolean
  onResize: (delta: number, colIndex: number) => void
  setResizing: (resizing: boolean) => void
  touchEnabled: boolean | null
}>({
  onResize: () => {},
  columnWidths: [],
  resizing: false,
  setResizing: () => {},
  touchEnabled: null,
})

export function useResizeContext() {
  return useContext(ResizeContext)
}

export function useColumnWidths(columnIndex: number) {
  const columnWidths = useResizeContext().columnWidths
  const columns = useColumns()
  const columnId = useMemo(() => columns[columnIndex], [columnIndex, columns])
  const index = useMemo(() => {
    const res = columnWidths.findIndex((c) => c.field === columnId)
    if (res === -1) throw new Error(`Column width not found for index ${columnId}`)
    return res
  }, [columnWidths, columnId])
  const res = useMemo(() => {
    const res_ = columnWidths[index]
    if (!res_) throw new Error(`Column width not found for index ${columnId}`)
    const { resizable, lastResizable, maxWidth, minWidth, width } = res_
    return { resizable, lastResizable, maxWidth, minWidth, width }
  }, [columnWidths, index, columnId])
  return res
}

function useLocalStorage<T>(key: string, initialValue: T) {
  const [value, setValue] = useState<T>(() => {
    let item: string | null = null
    try {
      item = localStorage.getItem(key)
    } catch {}
    return item ? (JSON.parse(item) as T) : initialValue
  })
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch (error) {
        console.error('Error saving to localStorage', error)
      }
    })
    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [key, value])
  return [value, setValue] as const
}

export function ResizeContextProvider<T extends Record<string, unknown>>({
  children,
  tableRef,
}: {
  children: ReactNode
  tableRef: RefObject<HTMLTableElement | null>
}) {
  const pivot = useIsPivot()
  const columns = useColumns<T>()
  const expandable = useExpandable()
  const id = useTableId()
  const [tableWidth, setTableWidth] = useState(0)
  const [resizing, setResizing] = useState(false)
  const entities = usePivotEntities<T>()
  const touchEnabled = useIsTouchDevice()

  const [requestedSize, setRequestedSize] = useLocalStorage<Record<string, number | undefined>>(
    `requestedSize-${id}-${pivot ? 'pivot' : ''}`,
    {}
  )

  const onResize = useCallback(
    (delta: number, colIndex: number) => {
      const columnId = columns[colIndex]!
      if (!delta) return
      setRequestedSize((prev) => {
        const prevSize = prev[columnId]
        if (prevSize === undefined) {
          return { ...prev, [columnId]: delta }
        }
        return { ...prev, [columnId]: prevSize + delta }
      })
    },
    [columns, setRequestedSize]
  )

  const columnWidths = useMemo(
    () => calculateColumnWidths(columns, entities, requestedSize, tableWidth, expandable, resizing),
    [columns, entities, requestedSize, tableWidth, expandable, resizing]
  )

  const handleResize = useCallback(
    (size: { width: number | undefined }) => {
      if (size.width === undefined) return
      setTableWidth(size.width)
    },
    [setTableWidth]
  )

  useResizeObserver({ ref: tableRef, onResize: handleResize })

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (resizing) return
      setRequestedSize((prev) => {
        const res: Record<string, number | undefined> = {}
        let anyChanged = false
        for (const col of columnWidths) {
          const prevSize = prev[col.field]
          if (prevSize !== col.width) {
            anyChanged = true
          }
          res[col.field] = col.width
        }
        if (anyChanged) {
          return { ...prev, ...res }
        }
        return prev
      })
    })
    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [columnWidths, setRequestedSize, resizing])

  return (
    <ResizeContext.Provider value={{ columnWidths, onResize, resizing, setResizing, touchEnabled }}>
      {children}
    </ResizeContext.Provider>
  )
}
